/**
 * Rebalance risk-profile enum and result copy.
 *
 * Belongs here: the five API values and how each one is shown.
 * Does not belong here: the allocation `AllocationRiskProfile` enum (no VERY_CONSERVATIVE).
 */
import { AllocationRiskProfile } from '../expert-review/types/allocation.types'
import { ALLOCATION_RISK_PROFILE_CONFIG } from '../expert-review/strategy-questionnaire/setup/config'

export enum RebalanceRiskProfile {
  VERY_CONSERVATIVE = 'VERY_CONSERVATIVE',
  CONSERVATIVE = 'CONSERVATIVE',
  BALANCED = 'BALANCED',
  AGGRESSIVE = 'AGGRESSIVE',
  VERY_AGGRESSIVE = 'VERY_AGGRESSIVE'
}

const REBALANCE_RISK_PROFILES = new Set<string>(Object.values(RebalanceRiskProfile))

/** Query `riskProfile` → enum. Anything else is the error screen. */
export function parseRebalanceRiskProfile(value: string | null): RebalanceRiskProfile | null {
  if (value != null && REBALANCE_RISK_PROFILES.has(value)) return value as RebalanceRiskProfile
  return null
}

export interface RebalanceRiskProfileCopy {
  name: string
  description: string
  assetSrc: string
}

const REBALANCE_TO_ALLOCATION_PROFILE: Record<RebalanceRiskProfile, AllocationRiskProfile> = {
  [RebalanceRiskProfile.VERY_CONSERVATIVE]: AllocationRiskProfile.CONSERVATIVE,
  [RebalanceRiskProfile.CONSERVATIVE]: AllocationRiskProfile.CONSERVATIVE,
  [RebalanceRiskProfile.BALANCED]: AllocationRiskProfile.BALANCED,
  [RebalanceRiskProfile.AGGRESSIVE]: AllocationRiskProfile.AGGRESSIVE,
  [RebalanceRiskProfile.VERY_AGGRESSIVE]: AllocationRiskProfile.VERY_AGGRESSIVE
}

const REBALANCE_RISK_PROFILE_ASSET_BASE = '/images/allocation/risk-profiles-v2/rebalance'

/** VERY_CONSERVATIVE uses conservative art — no separate asset. */
const REBALANCE_RISK_PROFILE_ASSET_SRC: Record<RebalanceRiskProfile, string> = {
  [RebalanceRiskProfile.VERY_CONSERVATIVE]: `${REBALANCE_RISK_PROFILE_ASSET_BASE}/rebalance-conservative.png`,
  [RebalanceRiskProfile.CONSERVATIVE]: `${REBALANCE_RISK_PROFILE_ASSET_BASE}/rebalance-conservative.png`,
  [RebalanceRiskProfile.BALANCED]: `${REBALANCE_RISK_PROFILE_ASSET_BASE}/rebalance-balanced.png`,
  [RebalanceRiskProfile.AGGRESSIVE]: `${REBALANCE_RISK_PROFILE_ASSET_BASE}/rebalance-aggressive.png`,
  [RebalanceRiskProfile.VERY_AGGRESSIVE]: `${REBALANCE_RISK_PROFILE_ASSET_BASE}/rebalance-very-aggressive.png`
}

/** Copy from allocation config; art from rebalance v2 assets. */
export function getRebalanceRiskProfileCopy(profile: RebalanceRiskProfile): RebalanceRiskProfileCopy {
  const copy = ALLOCATION_RISK_PROFILE_CONFIG[REBALANCE_TO_ALLOCATION_PROFILE[profile]]
  const assetSrc = REBALANCE_RISK_PROFILE_ASSET_SRC[profile]
  if (profile === RebalanceRiskProfile.VERY_CONSERVATIVE) {
    return {
      name: 'Very Conservative',
      description:
        '<span style="font-weight:500;color:rgba(0,0,0,0.7);">Built to preserve capital by keeping risk to a minimum. </span>' +
        '<span style="font-weight:400;color:rgba(0,0,0,0.7);">Ideal when your life stage requires capital to be accessible to meet needs anytime.</span>',
      assetSrc
    }
  }
  return { ...copy, assetSrc }
}
